import {
  listConnectChildFolders,
  type ConnectChildFolder,
} from '@data-access/connect/tc-tcps';
import {
  useApiServices,
  useFxWorkspaceContext,
} from '@mobile-mapping/providers';
import { useQuery } from '@tanstack/solid-query';
import { BtnClasses, CustomInput, Popup } from '@tcweb/connect-ui';
import { useConnectProject } from '@trimble/connect-project-providers';
import { t } from '@trimble/i18n';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import {
  SelectionTree,
  type TreeNodeDefinition,
} from '@trimble/ui-components/solidjs';
import { Component, For, Match, Show, Switch, createSignal } from 'solid-js';
import { Portal } from 'solid-js/web';

const CREATE_FX_WORKSPACE_FOLDERS_QUERY_KEY = 'create-fx-workspace-folders';
const FOLDER_SKELETON_WIDTHS = ['w-2/3', 'w-1/2', 'w-3/5', 'w-2/5'] as const;

function toFolderTreeNode(
  folder: ConnectChildFolder,
  parentId?: string
): TreeNodeDefinition {
  return {
    id: folder.id,
    parentId,
    label: folder.name,
    isLeaf: false,
    allowSelectFolder: true,
    showVisibilityToggle: false,
  };
}

export const CreateFxWorkspaceDialog: Component<{
  show: boolean;
  onClose(): void;
}> = (props) => {
  return (
    <Show when={props.show}>
      <CreateFxWorkspaceDialogContent onClose={props.onClose} />
    </Show>
  );
};

const CreateFxWorkspaceDialogContent: Component<{
  onClose(): void;
}> = (props) => {
  const fxWorkspace = useFxWorkspaceContext();
  const { getTcpsRegionClient } = useApiServices();
  const { selectedRegion, selectedProject } = useConnectProject();
  const [name, setName] = createSignal('');
  const [selectedFolderId, setSelectedFolderId] = createSignal<
    string | undefined
  >(undefined);
  const [isSaving, setIsSaving] = createSignal(false);

  const foldersQuery = useQuery(() => {
    const region = selectedRegion();
    const folderId = selectedProject()?.rootId;
    const enabled = region !== undefined && folderId !== undefined;

    return {
      enabled,
      retry: false,
      queryKey: [
        CREATE_FX_WORKSPACE_FOLDERS_QUERY_KEY,
        region?.location ?? 'none',
        folderId ?? 'none',
      ],
      queryFn: async (): Promise<readonly ConnectChildFolder[]> => {
        if (region === undefined || folderId === undefined) {
          return [];
        }

        const client = await getTcpsRegionClient(region);
        return listConnectChildFolders(client, folderId);
      },
    };
  });

  const loadChildFolders = async (
    nodeId: string
  ): Promise<readonly TreeNodeDefinition[]> => {
    const region = selectedRegion();
    if (region === undefined) {
      return [];
    }

    const client = await getTcpsRegionClient(region);
    const folders = await listConnectChildFolders(client, nodeId);
    return folders.map((folder) => toFolderTreeNode(folder, nodeId));
  };

  const handleClose = (): void => {
    if (isSaving()) {
      return;
    }
    props.onClose();
  };

  const projectHasNoFolders = (): boolean =>
    foldersQuery.status === 'success' && (foldersQuery.data?.length ?? 0) === 0;

  const isRootFolderLoadPending = (): boolean =>
    foldersQuery.status === 'pending' ||
    (foldersQuery.status === 'error' &&
      foldersQuery.fetchStatus === 'fetching');

  const handleCreate = async (): Promise<void> => {
    const trimmed = name().trim();
    const folderId = selectedFolderId();
    if (
      !trimmed ||
      isSaving() ||
      (folderId === undefined && !projectHasNoFolders())
    ) {
      return;
    }

    setIsSaving(true);
    try {
      const created =
        folderId === undefined
          ? await fxWorkspace.createWorkspace(trimmed)
          : await fxWorkspace.createWorkspace(trimmed, folderId);
      if (created) {
        props.onClose();
        return;
      }

      NotificationEventBus.emit({
        type: 'error',
        message: t('mobileMapping.fxWorkspace.createFailed', {
          name: trimmed,
        }),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleNameKeyDown = (
    event: KeyboardEvent & { currentTarget: HTMLInputElement; target: Element }
  ): void => {
    if (event.key !== 'Enter' || event.isComposing) {
      return;
    }

    event.preventDefault();
    void handleCreate();
  };

  const canCreate = (): boolean =>
    !isSaving() &&
    name().trim().length > 0 &&
    (selectedFolderId() !== undefined || projectHasNoFolders());

  return (
    <Portal>
      <Popup
        show={true}
        name="create-fx-workspace"
        heading={t('mobileMapping.fxWorkspace.createWorkspaceTitle')}
        role="dialog"
        aria-modal={true}
        overlayClass="z-modal"
        closeOnBackdropClick={!isSaving()}
        backdropAndCloseDisabled={isSaving()}
        content={
          <div class="flex flex-col gap-3">
            <CustomInput
              name="fx-workspace-name"
              label={t('mobileMapping.fxWorkspace.nameLabel')}
              placeholder={t('mobileMapping.fxWorkspace.namePlaceholder')}
              value={name()}
              onInput={(
                event: InputEvent & {
                  currentTarget: HTMLInputElement;
                }
              ) => setName(event.currentTarget.value)}
              onKeyDown={handleNameKeyDown}
            />
            <div data-testid="fx-workspace-folder-list">
              <div
                class="mb-1"
                textContent={t('mobileMapping.fxWorkspace.folderLabel')}
              />
              <Show
                when={projectHasNoFolders()}
                fallback={
                  <div class="max-h-60 overflow-auto">
                    <Switch>
                      <Match when={isRootFolderLoadPending()}>
                        <For each={FOLDER_SKELETON_WIDTHS}>
                          {(width) => (
                            <div
                              data-testid="selection-tree-loading-skeleton"
                              class="flex h-9 items-center animate-pulse"
                              aria-busy="true"
                            >
                              <div
                                class={`ml-2 h-4 rounded-sm bg-trimble-gray-200 ${width}`}
                              />
                            </div>
                          )}
                        </For>
                      </Match>
                      <Match when={foldersQuery.status === 'error'}>
                        <div
                          data-testid="fx-workspace-folder-load-error"
                          class="flex h-9 items-center gap-2 pr-2 text-sm text-error"
                          role="alert"
                        >
                          <span
                            class="truncate"
                            textContent={t(
                              'mobileMapping.fxWorkspace.folderLoadFailed'
                            )}
                          />
                          <button
                            type="button"
                            class="shrink-0 cursor-pointer text-sm text-blue-600 underline"
                            data-testid="fx-workspace-folder-load-retry"
                            onClick={() => {
                              void foldersQuery.refetch();
                            }}
                          >
                            {t('general.retry')}
                          </button>
                        </div>
                      </Match>
                      <Match when={foldersQuery.status === 'success'}>
                        <SelectionTree
                          multiSelect={false}
                          rowMode="pick"
                          childrenLoadStatus="success"
                          nodes={(foldersQuery.data ?? []).map((folder) =>
                            toFolderTreeNode(folder)
                          )}
                          loadChildren={loadChildFolders}
                          isNodeLoading={() => false}
                          isNodeVisible={() => false}
                          onVisibilityToggle={() => undefined}
                          isNodeSelected={(nodeId) =>
                            nodeId === selectedFolderId()
                          }
                          onNodeSelect={(nodeId) => {
                            setSelectedFolderId(nodeId);
                          }}
                        />
                      </Match>
                    </Switch>
                  </div>
                }
              >
                <div
                  data-testid="fx-workspace-no-folders"
                  textContent={t('mobileMapping.fxWorkspace.noFolders')}
                />
              </Show>
            </div>
          </div>
        }
        primaryBtnLabel={t('mobileMapping.fxWorkspace.createWorkspace')}
        primaryBtnClass={BtnClasses.PRIMARY}
        primaryBtnDisabled={!canCreate()}
        secondaryBtnLabel={t('general.cancel')}
        secondaryBtnClass={BtnClasses.SECONDARY}
        secondaryBtnDisabled={isSaving()}
        onPrimaryAction={() => void handleCreate()}
        onSecondaryAction={handleClose}
        onClose={handleClose}
      />
    </Portal>
  );
};
